import React, { useCallback, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Avatar from "@mui/material/Avatar";
import IconButton from "@mui/material/IconButton";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import Langs from "../../../app/lang/langs";
import { ORANGE, HEADER_HEIGHT, WEEKLY_COLLAB_COL, WEEKLY_DAY_COL, MONTHLY_COLLAB_COL, MONTHLY_DAY_COL, ROW_HEIGHT } from "./MobileScheduler/constants";
import { getInitials, getAbbreviatedName, formatTime, formatTimeFromMinutes } from "./MobileScheduler/utils/schedulerHelpers";
import { DayHeaderCell, TeamHeaderRow, ShiftChip } from "./MobileScheduler/components";
import useSchedulerDrag from "./MobileScheduler/hooks/useSchedulerDrag";
import {
  formatDate,
  getFormattedDate,
  isWorkingShift,
  isOwner,
  checkPermission,
  checkIfPublicHoliday,
  generateUniqueId,
  showErrorToast,
} from "../../../app/utils/helpers";
import {
  setShowFormPopup,
  toggleSelectedShift,
  updateShiftProperties,
  fillPlanningList,
} from "../planningSlice";
import { showLeaveModal } from "../../leaves/leavesSlice";
import { addShifts, updateShifts } from "../../../components/scheduler/shifts/shiftsSlice";
import { getCollabReport } from "../../reports/reportsSlice";
import { config } from "../../../app/utils/config";
import { ConfirmModal } from "../../../components/popups/ConfirmModal";

// ─── Component ──────────────────────────────────────────────────────
export default function MobileScheduler() {
  const dispatch = useDispatch();

  // ── Redux selectors ──
  const WEEKLY_VIEW_DAYS = useSelector((state) => state.planning.WEEKLY_VIEW_DAYS);
  const selectedDate = useSelector((state) => state.planning.selectedDate);
  const selectedView = useSelector((state) => state.planning.selectedView);
  const selectedGroupe = useSelector((state) => state.planning.selectedGroupe);
  const planningList = useSelector((state) => state.planning.planningList);
  const selectedShifts = useSelector((state) => state.planning.selectedShifts);
  const draggingItem = useSelector((state) => state.planning.draggingItem);
  const publications = useSelector((state) => state.planning.publications);
  const collabs = useSelector((state) => state.collabs.list);
  const resources = useSelector((state) => state.collabs.collaboratorsList);
  const selectedTeamsIds = useSelector((state) => state.teams.selectedTeamsIds);
  const teams = useSelector((state) => state.teams);
  const groupesList = useSelector((state) => state.groupes.list);
  const userRoleId = useSelector((state) => state.login.userRoleId);
  const userId = useSelector((state) => state.login.userId);
  const settings = useSelector((state) => state.settings);
  const sampleShifts = useSelector((state) => state.sampleShifts.list);
  const sampleLeaves = useSelector((state) => state.sampleLeaves.list);
  const dynamicLeaves = useSelector((state) => state.sampleLeaves.dynamicLeaves);
  const allTeams = useSelector((state) => state.teams.allTeams);

  // ── Local state ──
  const [showConfirmAddShiftPopup, setShowConfirmAddShiftPopup] = useState(false);
  const [showConfirmReplaceShiftPopup, setShowConfirmReplaceShiftPopup] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [pendingSelectedShift, setPendingSelectedShift] = useState(null);
  const [newShifts, setNewShifts] = useState([]);
  const [shiftToReplace, setShiftToReplace] = useState(null);

  const containerRef = useRef(null);


  // ── Dimensions ──
  const isWeekly = selectedView === "WEEKLY";
  const collabColWidth = isWeekly ? WEEKLY_COLLAB_COL : MONTHLY_COLLAB_COL;
  const dayColWidth = isWeekly ? WEEKLY_DAY_COL : MONTHLY_DAY_COL;

  // ── Build days array ──
  const daysInView = useMemo(() => {
    const days = [];
    const start = new Date(selectedDate);
    if (isWeekly) {
      for (let i = 0; i < WEEKLY_VIEW_DAYS; i++) {
        const d = new Date(start);
        d.setDate(start.getDate() + i);
        days.push(d.getTime());
      }
    } else {
      const month = start.getMonth();
      const year = start.getFullYear();
      const d = new Date(year, month, 1);
      while (d.getMonth() === month) {
        days.push(d.getTime());
        d.setDate(d.getDate() + 1);
      }
    }
    return days;
  }, [selectedDate, isWeekly, WEEKLY_VIEW_DAYS]);

  // ── View range boundaries ──
  const viewRange = useMemo(() => {
    const from = new Date(selectedDate);
    const to = new Date(selectedDate);
    if (isWeekly) {
      to.setDate(from.getDate() + WEEKLY_VIEW_DAYS);
    } else {
      to.setMonth(from.getMonth() + 1);
    }
    return { from, to };
  }, [selectedDate, isWeekly, WEEKLY_VIEW_DAYS]);

  // ── Build shiftsByResource map ──
  const shiftsByResource = useMemo(() => {
    const map = new Map();
    for (const shift of planningList) {
      const key = shift.id_collab;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(shift);
    }
    return map;
  }, [planningList]);

  // ── Get team from ID ──
  const getTeamFromId = useCallback(
    (teamId) => {
      return teams.list && teams.list.find((t) => t.id === teamId);
    },
    [teams.list]
  );

  // ── Get team settings for shift counting ──
  const getTeamHours = useCallback(
    (teamId) => {
      const team = getTeamFromId(teamId);
      let heureDebut = 9, heureMidi = 14, heureFin = 17, heureDebutNuit = 22, heureFinNuit = 7;
      if (team && team.settings) {
        team.settings.forEach((item) => {
          if (item.label === "Heure début de journée") heureDebut = Number(item.value);
          if (item.label === "Heure mi-journée") heureMidi = Number(item.value);
          if (item.label === "Heure fin de journée") heureFin = Number(item.value);
          if (item.label === "Heure début nuit") heureDebutNuit = Number(item.value);
          if (item.label === "Heure fin nuit") heureFinNuit = Number(item.value);
        });
      }
      return { heureDebut, heureMidi, heureFin, heureDebutNuit, heureFinNuit };
    },
    [getTeamFromId]
  );

  // ── Get country ID from team ──
  const getIdCountryFromTeamId = useCallback(
    (teamId) => {
      const team = getTeamFromId(teamId);
      return team ? team.id_country || 1 : 1;
    },
    [getTeamFromId]
  );

  // ── Check if a day is a day off ──
  const checkDayOff = useCallback(
    (timestamp, idCountry) => {
      const date = moment(timestamp, "x");
      const weekDay = date.day();
      if (weekDay === 0 || weekDay === 6) return true;
      return checkIfPublicHoliday(settings.holidays[date.year()], date, idCountry);
    },
    [settings.holidays]
  );

  // ── Permission checks ──
  const isManager = useCallback(
    (teamId, scope) => {
      if (!scope) scope = config.scopes.shifts;
      return checkPermission(scope, config.permissionTypes.edit, teamId);
    },
    []
  );

  const canEditPlanning = useCallback(
    (teamId) => {
      if (isOwner(userRoleId)) return true;
      if (!settings.blockageDay) return false;
      const now = moment();
      const monthEnd = moment(selectedDate, "x").endOf("month").add(settings.blockageDay, "day");
      return isManager(teamId) && now.isBefore(monthEnd);
    },
    [userRoleId, settings.blockageDay, selectedDate, isManager]
  );

  // ── Filter collabs based on selectedGroupe / selectedTeamsIds ──
  const filteredCollabs = useMemo(() => {
    const viewFrom = viewRange.from.getTime();
    const viewTo = viewRange.to.getTime();

    const filterByDates = (collab) => {
      const entryDate = collab.ancienneteeDate ? new Date(collab.ancienneteeDate).getTime() : null;
      const exitDate = collab.exitDate ? new Date(collab.exitDate).getTime() : null;
      if (entryDate && entryDate >= viewTo) return false;
      if (exitDate && exitDate <= viewFrom) return false;
      return true;
    };

    if (selectedGroupe === 0) {
      const result = [];
      const seen = {};
      for (const teamId of selectedTeamsIds) {
        const teamCollabs = collabs.filter((collab) => {
          if (!filterByDates(collab)) return false;
          if (
            (collab.id_team === teamId && !collab.id_team_delegate) ||
            (collab.id_team_delegate &&
              collab.id_team_delegate !== collab.id_team &&
              collab.id_team_delegate === teamId)
          ) {
            if (!seen[collab.id]) {
              seen[collab.id] = true;
              return true;
            }
          }
          return false;
        });
        if (teamCollabs.length) {
          result.push({ teamId, collabs: teamCollabs });
        }
      }
      return result;
    } else {
      const groupe = groupesList.find((g) => g.id === selectedGroupe);
      if (!groupe) return [];
      const groupeCollabs = resources.filter((r) => {
        if (!filterByDates(r)) return false;
        return r.groupeIds && r.groupeIds.includes(selectedGroupe);
      });
      return [{ teamId: groupe.id_team, collabs: groupeCollabs, linkedGroupes: groupe.linked_groups }];
    }
  }, [collabs, resources, selectedGroupe, selectedTeamsIds, groupesList, viewRange]);

  // ── Get team label with publication date ──
  const getTeamLabelFromId = useCallback(
    (teamId) => {
      let label = "";
      const team = teams.list && teams.list.find((t) => t.id === teamId);
      if (team) label = team.label;
      if (publications[teamId]) {
        const pubDate = new Date(publications[teamId].created_at);
        label = (
          <Langs
            str="LABEL_WITH_LATEST_PUBLISH"
            params={{ LABEL: label, DATE: formatDate(pubDate, "DD-MM-YYYY HH:mm") }}
          />
        );
      }
      return label;
    },
    [teams.list, publications]
  );

  // ── Get shifts for a resource on a specific date ──
  const getResourceShiftsForDate = useCallback(
    (dateTimestamp, resource, teamId) => {
      const date = new Date(dateTimestamp);
      const formattedDate = getFormattedDate(date);
      const formattedViewFrom = getFormattedDate(viewRange.from);
      const team = getTeamFromId(teamId);

      let collabTimezone = config.timezone;
      if (team) {
        switch (team.id_country) {
          case 3:
            collabTimezone = "Indian/Mauritius";
            break;
          default:
            break;
        }
      }

      const resourceId = resource.value || resource.id;
      const allShifts = shiftsByResource.get(resourceId);
      if (!allShifts) return [];

      return allShifts.filter((shift) => {
        const fromDate = moment(shift.from, "x").toDate();
        const toDate = moment(shift.to, "x").toDate();

        let formattedFromDate = getFormattedDate(fromDate);
        if (shift.id_sample_leave) {
          formattedFromDate = moment(shift.from, "x").tz(collabTimezone).format("DD-MM-YYYY");
        }

        const onlyTeamShifts =
          checkPermission(config.scopes.shifts, config.permissionTypes.view, teamId) ||
          ((shift.id_team === teamId && shift.id_team_delegate !== teamId) ||
            shift.id_team_delegate === teamId);

        if (!onlyTeamShifts) return false;

        // Leaves span multiple days — show the shift on every day it overlaps
        if (shift.id_sample_leave) {
          const dayStart = new Date(date);
          dayStart.setHours(0, 0, 0, 0);
          const dayEnd = new Date(date);
          dayEnd.setHours(23, 59, 59, 999);
          return fromDate <= dayEnd && toDate >= dayStart;
        }

        return (
          formattedFromDate === formattedDate ||
          (formattedViewFrom === formattedDate &&
            fromDate.getTime() <= viewRange.from.getTime() &&
            toDate.getTime() >= viewRange.from.getTime())
        );
      });
    },
    [shiftsByResource, viewRange, userRoleId, getTeamFromId]
  );

  // ── Get shift color ──
  const getShiftColor = useCallback(
    (shift) => {
      if (shift.type === "LEAVE") return "#ffffff";
      if (shift.absent) return "var(--red-400)";
      if (shift.type === "R") return "#ffffff";
      if (["AST", "INT_AST"].includes(shift.type)) {
        for (const key in settings.shiftTypes) {
          if (settings.shiftTypes[key].value === "AST") return settings.shiftTypes[key].color;
        }
        return "#ccc";
      }
      let cellColor = shift.color;
      if (!cellColor) {
        if (shift.id_sample_leave) {
          for (const key in sampleLeaves) {
            if (sampleLeaves[key].id === shift.id_sample_leave && sampleLeaves[key].color) {
              cellColor = sampleLeaves[key].color;
              break;
            }
          }
        } else {
          for (const key in sampleShifts) {
            if (sampleShifts[key].id === shift.id_sample_shift && sampleShifts[key].color) {
              cellColor = sampleShifts[key].color;
              break;
            }
          }
        }
      }
      return cellColor || "var(--darkGrey)";
    },
    [settings.shiftTypes, sampleShifts, sampleLeaves]
  );

  // ── Get shift label ──
  const getShiftLabel = useCallback(
    (shift) => {
      // Match desktop: check type-based labels first (regardless of id_sample_leave)
      if (shift.type === "LEAVE") return "Congé";
      if (dynamicLeaves[shift.type]) return dynamicLeaves[shift.type].description;
      if (["AST"].includes(shift.type)) {
        for (const key in settings.shiftTypes) {
          if (settings.shiftTypes[key].value === shift.type) {
            return settings.shiftTypes[key].label;
          }
        }
        return "Astreinte";
      }
      if (shift.task) return shift.task;
      if (shift.id_team_delegate) {
        const teamsFiltered = allTeams && allTeams.filter((t) => t.id === shift.id_team_delegate);
        if (teamsFiltered && teamsFiltered[0]) {
          return teamsFiltered[0].label.split(".")[0];
        }
      }
      return "";
    },
    [dynamicLeaves, settings.shiftTypes, allTeams]
  );

  // ── Get shift time display ──
  const getShiftTimeDisplay = useCallback(
    (shift) => {
      if (["AST", "LEAVE"].includes(shift.type) || dynamicLeaves[shift.type]) return "";
      if (shift.type === "s") return "Journée";
      let startTime = "";
      let endTime = "";
      if (shift.timeFrom != null) {
        startTime = formatTimeFromMinutes(shift.timeFrom);
      } else if (shift.from) {
        startTime = formatTime(shift.from);
      }
      if (shift.timeTo != null) {
        endTime = formatTimeFromMinutes(shift.timeTo);
      } else if (shift.to) {
        endTime = formatTime(shift.to);
      }
      if (startTime && endTime) return `${startTime}-${endTime}`;
      return startTime || endTime || "";
    },
    [dynamicLeaves]
  );

  // ── Validation logic (from desktop Scheduler.js) ──
  const checkLegalGapBetweenShift = useCallback(
    (shift) => {
      if (isWorkingShift(shift)) {
        const legalGap = 11 * 60 * 60 * 1000;
        const from = shift.from - legalGap;
        const to = shift.to + legalGap;
        const conflicts = planningList.filter((s) => {
          return (
            isWorkingShift(s) &&
            s.id_collab === shift.id_collab &&
            (s.id_team === shift.id_team || s.id_team_delegate === shift.id_team_delegate) &&
            ((s.to > from && s.to < to) || (s.from < to && s.from > from))
          );
        });
        if (conflicts.length) return { valid: false };
      }
      return { valid: true };
    },
    [planningList]
  );

  const checkShiftWithinCollabDates = useCallback((shift, resource) => {
    if (!isWorkingShift(shift)) return { valid: true };
    if (resource.ancienneteeDate && (resource.exitDate === null || resource.exitDate)) {
      const entryDateMillis = new Date(resource.ancienneteeDate).getTime();
      const exitDateMillis = resource.exitDate ? new Date(resource.exitDate).getTime() : Infinity;
      if (shift.from >= entryDateMillis && (resource.exitDate === null || shift.to <= exitDateMillis)) {
        return { valid: true };
      }
    }
    return { valid: false };
  }, []);

  const validateShift = useCallback(
    (shift, resource) => {
      const legalGapCheck = checkLegalGapBetweenShift(shift);
      const withinCollabDatesCheck = checkShiftWithinCollabDates(shift, resource);
      let components = [];

      if (!legalGapCheck.valid) {
        components.push(<Langs key="legalGapWarn" str="SHIFT_LEGAL_GAP_WARN_MSG" />);
      }
      if (!withinCollabDatesCheck.valid) {
        if (components.length > 0) components.push(<br key="separator" />);
        components.push(<Langs key="collabDateWarn" str="SHIFT_COLLAB_DATE_WARN_MSG" />);
      }

      let errMsg = null;
      if (components.length > 0) {
        errMsg = <div>{components}</div>;
      }

      return {
        isValid: legalGapCheck.valid && withinCollabDatesCheck.valid,
        errorMessage: errMsg,
      };
    },
    [checkLegalGapBetweenShift, checkShiftWithinCollabDates]
  );

  // ── Add shift dispatch ──
  const handleAddShiftDispatch = useCallback(
    (addedShifts) => {
      setShowConfirmAddShiftPopup(false);
      if (!addedShifts && newShifts.length) {
        addedShifts = newShifts;
      }
      if (addedShifts && addedShifts.length) {
        dispatch(addShifts({ items: addedShifts })).then((response) => {
          if (response?.payload?.responseError) {
            showErrorToast();
            if (pendingSelectedShift) {
              dispatch(updateShiftProperties(pendingSelectedShift));
              setPendingSelectedShift(null);
            }
          } else {
            dispatch(fillPlanningList(addedShifts));
            setNewShifts([]);
          }
        });
      }
    },
    [dispatch, newShifts, pendingSelectedShift]
  );

  const handleCancelAddShift = useCallback(() => {
    if (pendingSelectedShift) {
      dispatch(updateShiftProperties(pendingSelectedShift));
      setPendingSelectedShift(null);
    }
    setShowConfirmAddShiftPopup(false);
    setNewShifts([]);
  }, [dispatch, pendingSelectedShift]);

  // ── Update shift dispatch (for drag) ──
  const handleUpdateShiftDispatch = useCallback(
    (data, shift, resource) => {
      if (!data && shiftToReplace) {
        data = shiftToReplace.data;
        shift = shiftToReplace.shift;
        resource = shiftToReplace.resource;
      }
      if (!data || !shift || !resource) return;

      data.status = "UPDATED";
      data.updatedAt = new Date().getTime();
      dispatch(updateShifts({ items: [data] })).then((response) => {
        if (response?.payload?.responseError) {
          showErrorToast();
          dispatch(updateShiftProperties(shift));
        } else {
          dispatch(updateShiftProperties(data));
        }
      });
      setShiftToReplace(null);
      setShowConfirmReplaceShiftPopup(false);
    },
    [dispatch, shiftToReplace]
  );

  const handleCancelUpdateShift = useCallback(() => {
    if (shiftToReplace && shiftToReplace.shift) {
      dispatch(updateShiftProperties(shiftToReplace.shift));
    }
    setShiftToReplace(null);
    setShowConfirmReplaceShiftPopup(false);
  }, [dispatch, shiftToReplace]);

  // ── Handle shift drop from SamplesRow (draggingItem from Redux) ──
  const handleSampleDrop = useCallback(
    (dayTs, resource, teamId) => {
      if (!draggingItem) return;
      const timeFrom = draggingItem.timeFrom;
      const timeTo = draggingItem.timeTo;
      const fromDate = new Date(dayTs);
      fromDate.setHours(parseInt(timeFrom / 60));
      fromDate.setMinutes(parseInt(timeFrom % 60));
      const toDate = new Date(fromDate.getTime());

      if (timeTo < timeFrom) {
        toDate.setHours(parseInt(timeTo / 60) + 24);
      } else {
        toDate.setHours(parseInt(timeTo / 60));
      }
      toDate.setMinutes(parseInt(timeTo % 60));

      const addedShift = {
        id: generateUniqueId(),
        id_collab: resource.value || resource.id,
        type: draggingItem.type,
        id_team: resource.id_team,
        from: fromDate.getTime(),
        to: toDate.getTime(),
        status: "CREATED",
        updatedAt: new Date().getTime(),
      };

      if (teamId !== resource.id_team) {
        addedShift.id_team_delegate = teamId;
      }
      if (selectedGroupe) {
        addedShift.id_groupe = selectedGroupe;
      }

      if (draggingItem.type === "R") {
        const rLeave = sampleLeaves.filter((s) => s.type === draggingItem.type)[0];
        if (rLeave) addedShift.id_sample_leave = rLeave.id;
      } else {
        if (draggingItem.task) {
          addedShift.task = draggingItem.task;
          addedShift.id_sample_shift = draggingItem.id;
        } else {
          addedShift.id_sample_leave = draggingItem.id;
        }
      }

      const validationResult = validateShift(addedShift, resource);
      if (!validationResult.isValid) {
        setShowConfirmAddShiftPopup(true);
        setNewShifts([addedShift]);
        setErrorMessage(validationResult.errorMessage);
      } else {
        handleAddShiftDispatch([addedShift]);
      }
    },
    [draggingItem, selectedGroupe, sampleLeaves, validateShift, handleAddShiftDispatch]
  );

  // ── Build grid data: rows of collabs grouped by team ──
  const gridData = useMemo(() => {
    const rows = [];

    for (const teamGroup of filteredCollabs) {
      // Add team header row when showing all teams
      if (selectedGroupe === 0) {
        rows.push({
          type: "teamHeader",
          teamId: teamGroup.teamId,
          label: getTeamLabelFromId(teamGroup.teamId),
        });
      }

      for (const collab of teamGroup.collabs) {
        const cellsMap = {};
        for (const dayTs of daysInView) {
          let shifts = getResourceShiftsForDate(dayTs, collab, teamGroup.teamId);

          // Filter by groupe if not "all"
          if (selectedGroupe !== 0 && collab.groupeIds && collab.groupeIds.length) {
            const linkedGroupes = teamGroup.linkedGroupes || [];
            shifts = shifts.filter((s) => {
              if (s.id_sample_leave) return true;
              return s.id_groupe === selectedGroupe || linkedGroupes.includes(s.id_groupe);
            });
          }

          cellsMap[dayTs] = shifts;
        }

        rows.push({
          type: "collab",
          collab,
          teamId: teamGroup.teamId,
          cellsMap,
        });
      }
    }
    return rows;
  }, [filteredCollabs, daysInView, selectedGroupe, getResourceShiftsForDate, getTeamLabelFromId]);

  // ── Count shifts per period for day headers ──
  const dayCounters = useMemo(() => {
    const counters = {};
    const primaryTeamId = selectedTeamsIds[0];
    const hours = getTeamHours(primaryTeamId);

    for (const dayTs of daysInView) {
      let am = 0, pm = 0, night = 0;
      for (const row of gridData) {
        if (row.type !== "collab") continue;
        const shifts = row.cellsMap[dayTs] || [];
        for (const shift of shifts) {
          const from = new Date(shift.from);
          const to = new Date(shift.to);
          if (from.getHours() <= hours.heureDebut && to.getHours() >= hours.heureMidi) am++;
          if (from.getHours() <= hours.heureMidi && to.getHours() >= hours.heureDebutNuit) pm++;
          if (from.getHours() >= hours.heureFin && from.getHours() <= hours.heureDebutNuit && to.getHours() >= hours.heureFinNuit) night++;
        }
      }
      counters[dayTs] = { am, pm, night };
    }
    return counters;
  }, [daysInView, gridData, selectedTeamsIds, getTeamHours]);

  // ── Total grid width ──
  const totalWidth = collabColWidth + daysInView.length * dayColWidth;

  // ── Max shifts in a cell to compute row height ──
  const getRowHeight = useCallback(
    (row) => {
      if (row.type === "teamHeader") return 36;
      let maxShifts = 1;
      for (const dayTs of daysInView) {
        const shifts = row.cellsMap[dayTs] || [];
        if (shifts.length > maxShifts) maxShifts = shifts.length;
      }
      return Math.max(ROW_HEIGHT, maxShifts * 32 + 12);
    },
    [daysInView]
  );

  // ── Shift click handler (tap) ──
  const handleShiftClick = useCallback(
    (shift) => {
      if (shift.id_sample_leave) {
        dispatch(showLeaveModal({ type: "UPDATE_LEAVE", data: shift }));
      } else {
        let cellType = "SHIFT";
        if (shift.task && shift.timeFrom != null) cellType = "SAMPLE_SHIFT";
        else if (shift.timeFrom != null) return;
        if (isOwner(userRoleId) || checkPermission(config.scopes.shifts, config.permissionTypes.edit, shift.id_team) || cellType !== "SAMPLE_SHIFT") {
          dispatch(setShowFormPopup({ type: cellType, data: shift }));
        }
      }
    },
    [dispatch, userRoleId]
  );


  // ── Handle add user ──
  const handleAddUser = useCallback(() => {
    dispatch(setShowFormPopup({ type: "ADD_USER" }));
  }, [dispatch]);

  // ── Unique collabs count ──
  const uniqueCollabsCount = useMemo(() => {
    const seen = new Set();
    for (const row of gridData) {
      if (row.type === "collab") {
        seen.add(row.collab.id || row.collab.value);
      }
    }
    return seen.size;
  }, [gridData]);

  // ── Drag drop callbacks passed to the hook ──
  const onDropCompleted = useCallback(
    (data, shift, resource) => {
      const validationResult = validateShift(data, resource);
      if (!validationResult.isValid) {
        setErrorMessage(validationResult.errorMessage);
        setShiftToReplace({ data, shift, resource });
        setShowConfirmReplaceShiftPopup(true);
      } else {
        handleUpdateShiftDispatch(data, shift, resource);
      }
    },
    [validateShift, handleUpdateShiftDispatch]
  );

  const onShiftSelect = useCallback(
    (shift) => { dispatch(toggleSelectedShift(shift)); },
    [dispatch]
  );

  // ═══════════════════════════════════════════════════════════════════
  // TOUCH DRAG & DROP (via hook)
  // ═══════════════════════════════════════════════════════════════════

  const {
    isDragging,
    holdingShiftId,
    dragShift,
    ghostPos,
    dropTarget,
    ghostRef,
    dragActiveRef,
    handlePointerDown,
    handlePointerUp,
  } = useSchedulerDrag({
    canEditPlanning,
    dynamicLeaves,
    draggingItem,
    gridData,
    handleSampleDrop,
    onDropCompleted,
    onShiftSelect,
    dispatch,
  });

  // ═══════════════════════════════════════════════════════════════════
  // MAIN RENDER
  // ═══════════════════════════════════════════════════════════════════

  const groupe = groupesList.find((g) => g.id === selectedGroupe);

  return (
    <Box
      ref={containerRef}
      sx={{
        position: "relative",
        width: "100%",
        userSelect: "none",
        WebkitUserSelect: "none",
        backgroundColor: "var(--surface-ground, #f5f5f5)",
        pb: 4,
      }}
    >
      {/* ── Grid Table ── */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: `${collabColWidth}px repeat(${daysInView.length}, ${dayColWidth}px)`,
          minWidth: totalWidth,
          position: "relative",
        }}
      >
        {/* ═══ HEADER ROW ═══ */}

        {/* Corner cell: Collabs label + add user */}
        <Box
          sx={{
            position: "sticky",
            left: 0,
            top: 0,
            zIndex: 11,
            height: HEADER_HEIGHT,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "var(--surface-card, #fff)",
            borderBottom: "2px solid rgba(0,0,0,0.1)",
            borderRight: "1px solid rgba(0,0,0,0.08)",
            gap: 0.25,
          }}
        >
          <Typography
            variant="caption"
            sx={{
              fontSize: "0.65rem",
              fontWeight: 700,
              color: "text.secondary",
              textTransform: "uppercase",
              letterSpacing: "0.5px",
            }}
          >
            <Langs str="Collabs" />
          </Typography>
          <Typography
            variant="caption"
            sx={{ fontSize: "0.6rem", color: "text.disabled" }}
          >
            ({uniqueCollabsCount})
          </Typography>
          {selectedGroupe !== 0 &&
            (!groupe || isManager(groupe.id_team, config.scopes.groups)) && (
              <IconButton
                size="small"
                onClick={handleAddUser}
                sx={{
                  width: 24,
                  height: 24,
                  color: ORANGE,
                  mt: 0.25,
                }}
              >
                <PersonAddIcon sx={{ fontSize: 16 }} />
              </IconButton>
            )}
        </Box>

        {/* Day header cells */}
        {daysInView.map((dayTs) => {
          const dayMoment = moment(dayTs);
          const isToday = dayMoment.isSame(moment(), "day");
          const isDayOff = checkDayOff(dayTs, getIdCountryFromTeamId(selectedTeamsIds[0]));
          const counts = dayCounters[dayTs] || { am: 0, pm: 0, night: 0 };
          return (
            <DayHeaderCell
              key={`header-${dayTs}`}
              dayTs={dayTs}
              isWeekly={isWeekly}
              isToday={isToday}
              isDayOff={isDayOff}
              counts={counts}
            />
          );
        })}

        {/* ═══ DATA ROWS ═══ */}
        {gridData.map((row, rowIndex) => {
          if (row.type === "teamHeader") {
            return <TeamHeaderRow key={`team-${row.teamId}`} label={row.label} />;
          }

          // Collab row
          const collab = row.collab;
          const teamId = row.teamId;
          const rowH = getRowHeight(row);

          return (
            <React.Fragment key={`row-${collab.id || collab.value}-${teamId}-${rowIndex}`}>
              {/* Sticky collab name cell */}
              <Box
                sx={{
                  position: "sticky",
                  left: 0,
                  zIndex: 5,
                  height: rowH,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 0.25,
                  backgroundColor: rowIndex % 2 === 0
                    ? "var(--surface-card, #fff)"
                    : "#f5f5f5",
                  borderBottom: "1px solid rgba(0,0,0,0.06)",
                  borderRight: "1px solid rgba(0,0,0,0.08)",
                  px: 0.5,
                  cursor: (checkPermission(config.scopes.shifts, config.permissionTypes.edit, teamId) || (collab.id || collab.value) == userId) ? "pointer" : "default",
                }}
                onClick={() => {
                  const collabId = collab.id || collab.value;
                  if (!checkPermission(config.scopes.shifts, config.permissionTypes.edit, teamId) && collabId != userId) return;
                  dispatch(setShowFormPopup({ type: "PROFILE_DETAILS", data: collab }));
                  dispatch(getCollabReport({
                    monthDate: moment(selectedDate, "x").format("MM-YYYY"),
                    id_collab: collabId,
                    id_team: teamId,
                  }));
                }}
              >
                <Avatar
                  sx={{
                    width: isWeekly ? 28 : 22,
                    height: isWeekly ? 28 : 22,
                    fontSize: isWeekly ? "0.6rem" : "0.5rem",
                    fontWeight: 700,
                    backgroundColor: collab.color || ORANGE,
                    color: "#fff",
                    flexShrink: 0,
                  }}
                >
                  {getInitials(collab.label)}
                </Avatar>
                <Typography
                  variant="caption"
                  noWrap
                  sx={{
                    fontSize: isWeekly ? "0.55rem" : "0.5rem",
                    fontWeight: 600,
                    color: collab.color || "text.primary",
                    lineHeight: 1.1,
                    maxWidth: collabColWidth - 8,
                    textAlign: "center",
                  }}
                >
                  {getAbbreviatedName(collab.label)}
                </Typography>
              </Box>

              {/* Day cells for this collab */}
              {daysInView.map((dayTs) => {
                const shifts = row.cellsMap[dayTs] || [];
                const isDayOff = checkDayOff(dayTs, getIdCountryFromTeamId(teamId));
                const isToday = moment(dayTs).isSame(moment(), "day");
                const isDropHere =
                  isDragging &&
                  dropTarget &&
                  dropTarget.dayTs === dayTs &&
                  dropTarget.collabId === (collab.value || collab.id);
                // Also highlight all cells when a sample is being dragged from SamplesRow
                const isSampleDragActive = !!draggingItem && !isDragging;
                return (
                  <Box
                    key={`cell-${collab.id || collab.value}-${dayTs}`}
                    data-day={dayTs}
                    data-collab={collab.value || collab.id}
                    data-team={teamId}
                    sx={{
                      height: rowH,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "stretch",
                      justifyContent: "center",
                      px: isWeekly ? "3px" : "1px",
                      py: "4px",
                      backgroundColor: isDayOff
                        ? "rgba(158,158,158,0.06)"
                        : isSampleDragActive
                        ? `${ORANGE}08`
                        : rowIndex % 2 === 0
                        ? "#fff"
                        : "#f5f5f5",
                      borderBottom: "1px solid rgba(0,0,0,0.06)",
                      borderRight: "1px solid rgba(0,0,0,0.04)",
                      borderLeft: isToday ? `2px solid ${ORANGE}30` : "none",
                      transition: "background-color 0.15s ease, outline 0.15s ease",
                      // Drop target highlight (shift-to-shift drag)
                      ...(isDropHere && {
                        outline: `2px solid ${ORANGE}`,
                        backgroundColor: `${ORANGE}15`,
                        outlineOffset: -2,
                      }),
                      // Sample drag: subtle dashed border to indicate droppable
                      ...(isSampleDragActive && !isDropHere && {
                        outline: `1px dashed ${ORANGE}60`,
                        outlineOffset: -1,
                      }),
                      position: "relative",
                      minWidth: dayColWidth,
                    }}
                  >
                    {/* Render shift chips */}
                    {shifts.map((shift) => (
                      <ShiftChip
                        key={shift.id}
                        shift={shift}
                        collab={collab}
                        teamId={teamId}
                        isWeekly={isWeekly}
                        color={getShiftColor(shift)}
                        label={getShiftLabel(shift)}
                        timeDisplay={getShiftTimeDisplay(shift)}
                        isSelected={selectedShifts.includes(shift.id)}
                        isHolding={holdingShiftId === shift.id}
                        canDrag={canEditPlanning(teamId) && !(shift.id_sample_leave && !dynamicLeaves[shift.type])}
                        dragActiveRef={dragActiveRef}
                        onPointerDown={handlePointerDown}
                        onPointerUp={handlePointerUp}
                        onClick={handleShiftClick}
                        dynamicLeaves={dynamicLeaves}
                      />
                    ))}
                  </Box>
                );
              })}
            </React.Fragment>
          );
        })}
      </Box>

      {/* ── Empty state ── */}
      {gridData.filter((r) => r.type === "collab").length === 0 && (
        <Box sx={{ textAlign: "center", mt: 6, px: 3 }}>
          <Typography variant="body2" color="text.secondary">
            <Langs str="NO_PLANNING_DATA" />
          </Typography>
        </Box>
      )}

      {/* ═══ DRAG GHOST (portal to body) ═══ */}
      {isDragging &&
        dragShift &&
        createPortal(
          <Box
            ref={ghostRef}
            sx={{
              position: "fixed",
              left: ghostPos.x,
              top: ghostPos.y,
              zIndex: 9999,
              pointerEvents: "none",
              opacity: 0.9,
              transform: "scale(1.05)",
              boxShadow: "0 8px 24px rgba(0,0,0,0.3)",
              borderRadius: "8px",
              px: 1,
              py: 0.5,
              backgroundColor: getShiftColor(dragShift),
              minWidth: 60,
              maxWidth: 120,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Typography
              variant="caption"
              noWrap
              sx={{
                fontSize: "0.7rem",
                fontWeight: 700,
                color: "#fff",
                textShadow: "0 1px 2px rgba(0,0,0,0.4)",
              }}
            >
              {getShiftTimeDisplay(dragShift) || getShiftLabel(dragShift) || dragShift.type}
            </Typography>
          </Box>,
          document.body
        )}


      {/* ═══ CONFIRM MODALS ═══ */}
      <ConfirmModal
        show={showConfirmAddShiftPopup}
        title={<Langs str="ADD_SHIFT" />}
        message={errorMessage}
        onConfirm={() => handleAddShiftDispatch()}
        onHide={handleCancelAddShift}
      />
      <ConfirmModal
        show={showConfirmReplaceShiftPopup}
        title={<Langs str="REPLACE_SHIFT" />}
        message={errorMessage}
        onConfirm={() => handleUpdateShiftDispatch()}
        onHide={handleCancelUpdateShift}
      />
    </Box>
  );
}
